'use client';
import React from 'react';
import { Section, Container } from '@layout';
import { Tabs, MainHero, Heading } from '@ui';
import { sectionSlug } from '@lib/portfolioSections';
import { heroLine, readHeroBody, readHeroBg, type LayoutLocale } from '@lib/heroLocale';
import { t } from '@utils/translate';
import { renderTranslation } from '@utils/renderTranslation';
import { withOptionalId } from '@lib/withOptionalId';
import { PortfolioNav, PortfolioTab } from '@pages';

interface PortfolioImage {
  id: string;
  title: string;
  description: string;
  image_url: string;
  url?: string;
  target?: string;
  rel?: string;
  blurb_mw?: string;
}

interface PortfolioSubSection {
  title: string;
  description?: string;
  url?: string;
  portfolio_url?: string;
  portfolio_images?: PortfolioImage[];
}

interface PortfolioWrapperProps {
  sectionID?: string;
  className?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  /** Overrides translated `hero.hero_title` from portfolio locale when set. */
  headingText?: string;
  headingType?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /** Class for the section-nav heading inside `PortfolioNav`. */
  sectionNavHeadingClass?: string;
  isSpanish?: boolean;
}

const PortfolioWrapper: React.FC<PortfolioWrapperProps> = ({
  sectionID,
  className,
  classes = '',
  containerId,
  containerClasses,
  headingText,
  headingType = 'h1',
  sectionNavHeadingClass,
  isSpanish = false,
}) => {
  const locale: LayoutLocale = isSpanish ? 'es' : 'en';
  const ns = 'portfolio';
  const pageTitle =
    headingText ?? heroLine('hero_title', ns, locale) ?? '';
  const heroEyebrow = heroLine('hero_eyebrow', ns, locale);
  const heroBody = readHeroBody(ns, locale);
  const heroBlurbMw = heroLine('hero_blurb_mw', ns, locale);
  const heroBg = readHeroBg(ns, locale);

  const sections = t('sections', 'portfolio', locale) as Array<{
    portfolio_sections?: PortfolioSubSection[];
  }>;

  const portfolioRoot = Array.isArray(sections)
    ? sections.find((s) => Array.isArray(s?.portfolio_sections))
    : undefined;

  const portfolioSections = portfolioRoot?.portfolio_sections ?? [];

  const tabs = portfolioSections.map((section, idx) => {
    const label =
      String(renderTranslation(section?.title)) || section?.title || `Section ${idx + 1}`;

    const descVal = renderTranslation(section?.description);
    const description = descVal !== null ? String(descVal) : (section?.description ?? '');

    return {
      id: `portfolio-tab-${idx}`,
      label,
      panelId: `portfolio-panel-${idx}`,
      title: label,
      content: (
        <PortfolioTab
          title={label}
          description={description}
          locale={locale}
          sectionSlug={sectionSlug(section)}
          portfolioImages={section?.portfolio_images ?? []}
        />
      ),
    };
  });

  return (
    <>
      <MainHero
        className='portfolio-hero'
        hero_bg={heroBg?.hero_bg}
        hero_bg_2x={heroBg?.hero_bg_2x}
        main={
          <>
            <Heading
              as={headingType}
              eyebrow={heroEyebrow}
              title={pageTitle}
              description={
                heroBody.length > 0
                  ? heroBody.map((para) => renderTranslation(para) ?? para)
                  : undefined
              }
              descriptionClassName='hero-sub'
            />
            {heroBlurbMw ? (
              <p className='hero-sub mobile-only'>{heroBlurbMw}</p>
            ) : null}
          </>
        }
      />
      <div className='section-group'>
        <Section {...withOptionalId(sectionID)} className={className || classes}>
          <Container containerId={containerId} containerClasses={containerClasses}>
            {tabs.length > 0 ? (
              <Tabs
                tabs={tabs}
                id='portfolio-tablist'
                className=''
                persistKey='portfolio-active-tab'
              />
            ) : null}

            <PortfolioNav
              portfolioSections={portfolioSections}
              locale={locale}
              headingType='h4'
              headingClass='portfolio-section-nav-heading'
            />
          </Container>
        </Section>
      </div>
    </>
  );
};

export default PortfolioWrapper;
